<template>
    <ion-page>
        <ion-header :translucent="true">
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-menu-button color="primary"></ion-menu-button>
                </ion-buttons>
                <ion-title>Bill Discount Application</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content :fullscreen="true">
            <div id="container">
                <!-- Sleek Segment Tab controller -->
                <ion-segment :value="segment" @ionChange="segmentChanged($event)" class="ion-text-center" scrollable="true" style="margin:auto;" id="ionSegmentScroll" >
                    <ion-segment-button value="Pending" class="ion-text-center" style="margin:auto;">
                        <ion-label>Pending</ion-label>
                    </ion-segment-button>
                    <ion-segment-button value="AllApplications" class="ion-text-center" style="margin:auto;">
                        <ion-label>All Applications</ion-label>
                    </ion-segment-button>
                </ion-segment>
                
                <div class="ion-padding">
                    <!-- Premium skeleton loading state -->
                    <div v-if="loading && applications.length === 0">
                        <div v-for="n in 5" :key="n" class="skeleton-card">
                            <ion-card style="margin: 10px 0; padding: 15px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.05);">
                                <ion-card-header style="padding: 0 0 10px 0;">
                                    <ion-row>
                                        <ion-col size="8">
                                            <ion-skeleton-text animated style="width: 60%; height: 16px; border-radius: 4px;"></ion-skeleton-text>
                                        </ion-col>
                                        <ion-col size="4" class="ion-text-right">
                                            <ion-skeleton-text animated style="width: 80%; height: 20px; border-radius: 10px; float: right;"></ion-skeleton-text>
                                        </ion-col>
                                    </ion-row>
                                </ion-card-header>
                                <ion-card-content style="padding: 0;">
                                    <ion-skeleton-text animated style="width: 90%; height: 12px; margin-bottom: 8px; border-radius: 4px;"></ion-skeleton-text>
                                    <ion-skeleton-text animated style="width: 40%; height: 12px; margin-bottom: 8px; border-radius: 4px;"></ion-skeleton-text>
                                    <ion-skeleton-text animated style="width: 70%; height: 12px; border-radius: 4px;"></ion-skeleton-text>
                                </ion-card-content>
                            </ion-card>
                        </div>
                    </div>

                    <!-- Single unified DataTable for both views -->
                    <DataTable v-else class="p-datatable-sm custom-datatable" showGridlines :value="applications" :lazy="true"
                        :paginator="true" :rows="perPage" :totalRecords="total" @page="onPage($event)"
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        responsiveLayout="stack" :rowClass="data=>{ return 'dataRow'}" :stripedRows="true"
                        :loading="loading">
                        
                        <template #loading>
                            <div class="ion-text-center" style="padding: 2rem;">
                                <ion-spinner name="crescent" color="primary"></ion-spinner>
                                <div style="margin-top: 8px; font-weight: 500; color: var(--ion-color-step-600);">Updating applications...</div>
                            </div>
                        </template>
                        
                        <template #empty>
                            <div class="ion-text-center ion-padding" style="color: var(--ion-color-step-500); padding: 40px 0;">
                                <ion-icon :icon="documentTextOutline" style="font-size: 48px; opacity: 0.5; margin-bottom: 10px;"></ion-icon>
                                <p style="font-size: 16px; font-weight: 500; margin: 0;">No records found</p>
                                <p style="font-size: 12px; margin-top: 4px;">There are no bill discount applications in this view.</p>
                            </div>
                        </template>

                        <Column field="bill_application_id" header="Application ID">
                            <template #body="{data}">
                                <span class="app-id-badge">#{{ data.bill_application_id }}</span>
                            </template>
                        </Column>
                        <Column field="bill_no" header="Bill No"></Column>
                        <Column field="company" header="Company">
                            <template #body="{data}">
                                <div class="company-cell">
                                    <ion-icon :icon="businessOutline" color="medium" style="margin-right: 6px; font-size: 16px; vertical-align: middle;"></ion-icon>
                                    <span style="font-weight: 600; color: var(--ion-color-dark);">{{ data.company }}</span>
                                </div>
                            </template>
                        </Column>
                        <Column field="description" header="Description"></Column>
                        
                        <Column field="current_bill" header="Current Bill">
                            <template #body="{data}">
                                <span class="price-text">{{ formatCurrency(data.current_bill) }}</span>
                            </template>
                        </Column>
                        <Column field="paid" header="Paid">
                            <template #body="{data}">
                                <span class="price-text">{{ formatCurrency(data.paid) }}</span>
                            </template>
                        </Column>
                        <Column field="current_discount" header="Discount">
                            <template #body="{data}">
                                <span class="discount-text">{{ formatCurrency(data.current_discount) }}</span>
                            </template>
                        </Column>
                        <Column field="current_due" header="Current Due">
                            <template #body="{data}">
                                <span class="due-text">{{ formatCurrency(data.current_due) }}</span>
                            </template>
                        </Column>
                        <Column field="max_discount_amount" header="Max Discount">
                            <template #body="{data}">
                                <span class="price-text">{{ formatCurrency(data.max_discount_amount) }}</span>
                            </template>
                        </Column>
                        <Column field="status" header="Status">
                            <template #body="{data}">
                                <span :class="['status-badge', data.status.toLowerCase()]">
                                    {{ data.status }}
                                </span>
                            </template>
                        </Column>

                        <Column header="Approval List" style="padding:0px;">
                            <template #body="{data}">
                                <ion-button fill="clear" size="small" @click="viewDetails(data)" class="details-button">
                                    <ion-icon slot="start" :icon="eyeOutline"></ion-icon>
                                    View Flow
                                </ion-button>
                            </template>
                        </Column>

                        <Column field="created_by" header="Created By"></Column>
                        <Column field="created_at" header="Created At">
                            <template #body="{data}">
                                <span style="font-size: 12px; color: var(--ion-color-step-600);">
                                    {{ formatDateString(data.created_at) }}
                                </span>
                            </template>
                        </Column>
                        
                        <Column field="action" header="">
                            <template #body="{data}">
                                <div v-if="data.status == 'Pending' && data.action == true" class="action-buttons-container">
                                    <ion-button color="success" fill="solid" size="small" class="action-btn"
                                        @click="openApprovalModal(data.bill_application_id, 'Approved')">
                                        <ion-icon slot="start" :icon="checkmarkCircleOutline"></ion-icon>
                                        Approve
                                    </ion-button>
                                    <ion-button color="danger" fill="solid" size="small" class="action-btn"
                                        @click="openApprovalModal(data.bill_application_id, 'Declined')">
                                        <ion-icon slot="start" :icon="closeCircleOutline"></ion-icon>
                                        Decline
                                    </ion-button>
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <!-- Timeline / Stepper Details Modal -->
                <ion-modal :is-open="detailModalIsOpen" @didDismiss="closeDetailModal" class="detail-modal">
                    <ion-page>
                        <ion-header>
                            <ion-toolbar mode="ios">
                                <ion-title>Application Details</ion-title>
                                <ion-buttons slot="end">
                                    <ion-button @click="closeDetailModal" style="font-weight: 600;">Close</ion-button>
                                </ion-buttons>
                            </ion-toolbar>
                        </ion-header>
                        <ion-content class="ion-padding" v-if="selectedApplication" :fullscreen="true">
                            <!-- Premium Summary Card -->
                            <div class="detail-card">
                                <h2 class="company-title">{{ selectedApplication.company }}</h2>
                                <p class="app-desc">{{ selectedApplication.description }}</p>
                                
                                <div class="badge-row">
                                    <span class="app-id-badge">ID: #{{ selectedApplication.bill_application_id }}</span>
                                    <span :class="['status-badge', selectedApplication.status.toLowerCase()]">{{ selectedApplication.status }}</span>
                                </div>
                                
                                <div class="grid-details">
                                    <div class="grid-item">
                                        <span class="grid-label">Bill No</span>
                                        <span class="grid-value">{{ selectedApplication.bill_no }}</span>
                                    </div>
                                    <div class="grid-item">
                                        <span class="grid-label">Total Bill</span>
                                        <span class="grid-value">{{ formatCurrency(selectedApplication.current_bill) }}</span>
                                    </div>
                                    <div class="grid-item">
                                        <span class="grid-label">Discount</span>
                                        <span class="grid-value highlight">{{ formatCurrency(selectedApplication.current_discount) }}</span>
                                    </div>
                                    <div class="grid-item">
                                        <span class="grid-label">Current Due</span>
                                        <span class="grid-value due">{{ formatCurrency(selectedApplication.current_due) }}</span>
                                    </div>
                                    <div class="grid-item">
                                        <span class="grid-label">Paid Amount</span>
                                        <span class="grid-value">{{ formatCurrency(selectedApplication.paid) }}</span>
                                    </div>
                                    <div class="grid-item">
                                        <span class="grid-label">Max Discount</span>
                                        <span class="grid-value">{{ formatCurrency(selectedApplication.max_discount_amount) }}</span>
                                    </div>
                                </div>

                                <div class="metadata-footer">
                                    <span>Created By: <strong>{{ selectedApplication.created_by }}</strong></span>
                                    <span>Date: <strong>{{ formatDateString(selectedApplication.created_at) }}</strong></span>
                                </div>
                            </div>

                            <!-- Approval Flow Timeline Stepper -->
                            <div class="timeline-container">
                                <h3 class="timeline-title">
                                    <ion-icon :icon="documentTextOutline" style="margin-right: 6px; vertical-align: middle;"></ion-icon>
                                    Approval Process Tracking
                                </h3>
                                
                                <div class="timeline-stepper">
                                    <div v-for="(step, idx) in selectedApplication.approvalSteps" :key="idx" class="timeline-step">
                                        <!-- Connection line -->
                                        <div v-if="idx < selectedApplication.approvalSteps.length - 1" class="timeline-line"></div>
                                        
                                        <!-- Icon node -->
                                        <div :class="['timeline-node', step.status.toLowerCase()]">
                                            <ion-icon v-if="step.status === 'Approved'" :icon="checkmarkCircleOutline"></ion-icon>
                                            <ion-icon v-else-if="step.status === 'Declined'" :icon="closeCircleOutline"></ion-icon>
                                            <ion-icon v-else :icon="timeOutline"></ion-icon>
                                        </div>
                                        
                                        <!-- Step Content -->
                                        <div class="timeline-content">
                                            <div class="timeline-header">
                                                <span class="approver-name">{{ step.name }}</span>
                                                <span :class="['step-status-tag', step.status.toLowerCase()]">{{ step.status }}</span>
                                            </div>
                                            
                                            <!-- Comments -->
                                            <div v-if="step.message" class="timeline-message">
                                                "{{ step.message }}"
                                            </div>
                                            
                                            <div v-if="step.updatedAt" class="timeline-time">
                                                Updated: {{ formatDateString(step.updatedAt) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Decision Action Block inside details -->
                            <div v-if="selectedApplication.status === 'Pending' && selectedApplication.action === true" class="action-panel">
                                <h3>Your Decision Required</h3>
                                <p>Please review and confirm your decision for this discount request.</p>
                                <div class="action-buttons-row">
                                    <ion-button color="success" class="action-btn"
                                        @click="openApprovalModalFromDetails('Approved')">
                                        <ion-icon slot="start" :icon="checkmarkCircleOutline"></ion-icon>
                                        Approve
                                    </ion-button>
                                    <ion-button color="danger" class="action-btn"
                                        @click="openApprovalModalFromDetails('Declined')">
                                        <ion-icon slot="start" :icon="closeCircleOutline"></ion-icon>
                                        Decline
                                    </ion-button>
                                </div>
                            </div>
                        </ion-content>
                    </ion-page>
                </ion-modal>

                <!-- Input Message Modal (Confirm Approve/Decline) -->
                <ion-modal :is-open="modalIsOpen" @didDismiss="modalIsOpen = false" style="--height: auto; --max-height: 280px; --border-radius: 16px;">
                    <ion-page>
                        <ion-header>
                            <ion-toolbar mode="ios">
                                <ion-title>Confirm Action</ion-title>
                                <ion-buttons slot="end">
                                    <ion-button :disabled="submitting" @click="modalIsOpen = false">Cancel</ion-button>
                                </ion-buttons>
                            </ion-toolbar>
                        </ion-header>
                        <ion-content class="ion-padding" :fullscreen="true">
                            <ion-item lines="none" style="--background: transparent; margin-bottom: 12px;">
                                <ion-label position="stacked" style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">
                                    Remarks {{billData['action']=='Approved'?'(Optional)':'(Required)'}}
                                </ion-label>
                                <ion-textarea :disabled="submitting" v-model="billData['message']" placeholder="Enter reason or notes..." style="--background: var(--ion-color-light); border-radius: 8px; padding: 10px; border: 1px solid var(--ion-color-light-shade);"></ion-textarea>
                            </ion-item>
                            <ion-button :disabled="submitting" :color="billData['action'] === 'Approved' ? 'success' : 'danger'" expand="block" style="--border-radius: 8px; font-weight: bold;" @click="confirmAction(billData['id'],billData['action'])">
                                <ion-spinner v-if="submitting" name="crescent" slot="start" style="--color: white; margin-right: 8px;"></ion-spinner>
                                Confirm {{ billData['action'] }}
                            </ion-button>
                        </ion-content>
                    </ion-page>
                </ion-modal>
            </div>
        </ion-content>
    </ion-page>
</template>

<script>
    import {
        IonButtons,
        IonButton,
        IonContent,
        IonHeader,
        IonMenuButton,
        IonIcon,
        IonPage,
        IonTitle,
        IonToolbar,
        IonSegment,
        IonSegmentButton,
        IonLabel,
        IonRow,
        IonCol,
        IonItem,
        IonTextarea,
        IonModal,
        IonSpinner,
        IonSkeletonText,
        IonCard,
        IonCardHeader,
        IonCardContent,
        alertController,
    } from '@ionic/vue';
    import {
        defineComponent
    } from 'vue';
    import DataTable from 'primevue/datatable';
    import Column from 'primevue/column';
    import {
        ellipsisVertical,
        ellipsisVerticalOutline,
        chevronBack,
        chevronBackOutline,
        checkmarkCircleOutline,
        closeCircleOutline,
        timeOutline,
        arrowForwardOutline,
        eyeOutline,
        businessOutline,
        documentTextOutline,
    } from "ionicons/icons";

    export default defineComponent({
        name: 'BillDiscountApplicationList',
        components: {
            IonButtons,
            IonButton,
            IonContent,
            IonHeader,
            IonMenuButton,
            IonIcon,
            IonPage,
            IonTitle,
            IonSegment,
            IonSegmentButton,
            IonLabel,
            IonToolbar,
            DataTable,
            Column,
            IonRow,
            IonCol,
            IonItem,
            IonTextarea,
            IonModal,
            IonSpinner,
            IonSkeletonText,
            IonCard,
            IonCardHeader,
            IonCardContent,
        },
        props: {
            segment: {
                type: String,
                default: 'Pending'
            },
            applicationId: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                applications: [],
                loading: false,
                submitting: false,
                billData: {
                    message: null,
                    id: null,
                    action: null,
                },
                perPage: 10,
                total: 0,
                modalIsOpen: false,
                detailModalIsOpen: false,
                selectedApplication: null,
                
                // Icons mapping
                ellipsisVertical,
                ellipsisVerticalOutline,
                chevronBack,
                chevronBackOutline,
                checkmarkCircleOutline,
                closeCircleOutline,
                timeOutline,
                arrowForwardOutline,
                eyeOutline,
                businessOutline,
                documentTextOutline,
            }
        },
        watch: {
            segment: {
                handler(newSegment, oldSegment) {
                    if (newSegment && newSegment !== oldSegment) {
                        this.loadBillApplications(0);
                    }
                }
            }
        },
        ionViewWillEnter() {
            this.loadBillApplications(0);
        },
        methods: {
            loadBillApplications(page){
                this.loading = true;
                const data = {
                    'username': window.localStorage.username,
                    'password': window.localStorage.password,
                    '_segment': this.segment,
                    'application_id': this.applicationId,
                    'page': page + 1,
                    'per_page': this.perPage
                };
                
                // Pass skipLoading: true to prevent global overlay blocker, using local loader/skeletons instead
                return this.$http.post('BillDiscountApplication/list', data, { skipLoading: true }).then(response => {
                    console.log(response);
                    const lists = response.data.lists;
                    this.total = response.data.total_record;
                    const applications = [];
                    
                    for(const i of lists){
                        let action = false;
                        const approvalSteps = [];
                        
                        for(const j of i.approval_ids){
                            const step = {
                                id: j.id,
                                name: j.name,
                                status: 'Pending',
                                message: '',
                                createdAt: '',
                                updatedAt: ''
                            };
                            
                            for(const k of i.forword_to){
                                if(k.user_id == j.id){
                                    step.status = k.status;
                                    step.message = k.message;
                                    step.createdAt = k.created_at;
                                    step.updatedAt = k.updated_at;
                                }
                                if(i.auth_id == k.user_id && k.status == "Pending" && i.status == "Pending"){
                                    action = true;
                                }
                            }
                            approvalSteps.push(step);
                        }
                        
                        applications.push({
                            'bill_application_id': i.bill_application_id,
                            'bill_no': i.bill_no,
                            'auth_id': i.auth_id,
                            'company': i.company,
                            'current_bill': i.current_bill,
                            'paid': i.paid,
                            'current_discount': i.current_discount,
                            'current_due': i.current_due,
                            'created_at': i.created_at,
                            'created_by': i.created_by,
                            'description': i.description,
                            'max_discount_amount': i.max_discount_amount,
                            'executed_amount': i.executed_amount,
                            'executed_at': i.executed_at,
                            'status': i.status,
                            'approval_ids': i.approval_ids,
                            'forword_to': i.forword_to,
                            'action': action,
                            'approvalSteps': approvalSteps,
                        });
                    }
                    this.applications = applications;
                    this.loading = false;
                }).catch(err => {
                    console.error(err);
                    this.loading = false;
                });
            },
            async confirmAction(id, action) {
                if (action !== 'Approved' && (!this.billData.message || this.billData.message.trim() === '')) {
                    this.presentAlert('Remarks are required for declining an application.');
                    return;
                }
                
                const alert = await alertController.create({
                    header: 'Confirm ' + action,
                    message: `Are you sure you want to ${action.toLowerCase()} this application?`,
                    buttons: [
                        {
                            text: 'Cancel',
                            role: 'cancel'
                        },
                        {
                            text: 'Confirm',
                            handler: () => {
                                this.submitAction(id, action);
                            }
                        }
                    ]
                });
                await alert.present();
            },
            submitAction(id, action) {
                if (this.submitting) return;
                this.submitting = true;
                
                const data = {
                    'username': window.localStorage.username,
                    'password': window.localStorage.password,
                    'action': action,
                    'id': id,
                    'message': this.billData['message'],
                };
                
                this.$http.post('BillDiscountApplication/approvedOrDeclined', data, { skipLoading: true }).then(response => {
                    this.submitting = false;
                    if(response.status == 200){
                        this.modalIsOpen = false;
                        
                        // Close detail modal and reload list sequentially after overlay transition clears
                        setTimeout(() => {
                            this.closeDetailModal();
                            this.loadBillApplications(0);
                        }, 250);
                    }
                }).catch(err => {
                    this.submitting = false;
                    console.error(err);
                });
            },
            onPage(event) {
                this.loadBillApplications(event.page);
            },
            segmentChanged(event) {
                const targetValue = event.target.value;
                if (this.segment !== targetValue) {
                    this.$router.push(`/bill-discount-application/${targetValue}`);
                }
            },
            
            // UI Helpers
            viewDetails(app) {
                this.selectedApplication = app;
                this.detailModalIsOpen = true;
            },
            closeDetailModal() {
                this.detailModalIsOpen = false;
                this.selectedApplication = null;
            },
            openApprovalModal(id, action) {
                this.billData.id = id;
                this.billData.action = action;
                this.billData.message = null;
                this.modalIsOpen = true;
            },
            openApprovalModalFromDetails(action) {
                if (this.selectedApplication) {
                    this.openApprovalModal(this.selectedApplication.bill_application_id, action);
                }
            },
            formatCurrency(value) {
                if (value === undefined || value === null) return '-';
                const num = Number(value);
                if (isNaN(num)) return value;
                return '৳ ' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            },
            formatDateString(dateStr) {
                if (!dateStr) return '-';
                try {
                    return this.dateFormate(dateStr, 'd M, Y h:i a');
                } catch (e) {
                    return dateStr;
                }
            },

            async presentResponseAlert(type, message) {
                const alert = await alertController
                    .create({
                        header: type + '!',
                        message: message,
                        buttons: ['OK'],
                    });
                await alert.present();
            },
            dateFormate(data, formate) {
                if (!data) {
                    return '-';
                }
                const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct',
                    'Nov', 'Dec'
                ];
                const date = new Date(data);
                let newDate = '';
                const dateFormate = formate.split('');
                for (const field of dateFormate) {
                    if (field == 'd') {
                        if (date.getDate() < 10) {
                            newDate += '0' + date.getDate();
                        } else {
                            newDate += date.getDate();
                        }
                    } else if (field == 'm') {
                        const month = date.getMonth() + 1;
                        if (month < 10) {
                            newDate += '0' + month;
                        } else {
                            newDate += month;
                        }
                    } else if (field == 'M') {
                        newDate += monthsShort[date.getMonth()];
                    } else if (field == 'y') {
                        newDate += date.getFullYear().toString().substr(2);
                    } else if (field == 'Y') {
                        newDate += date.getFullYear();
                    } else if (field == 'H') {
                        if ((date.getHours()) < 10) {
                            newDate += '0' + date.getHours();
                        } else {
                            newDate += date.getHours();
                        }
                    } else if (field == 'h') {
                        let hours = date.getHours();
                        if (date.getHours() > 12) {
                            hours = hours - 12;
                        }
                        if (date.getHours() == 0) {
                            hours = 12;
                        }

                        if (hours < 10) {
                            newDate += '0' + hours;
                        } else {
                            newDate += hours;
                        }
                    } else if (field == 'i') {
                        if (date.getMinutes() < 10) {
                            newDate += '0' + date.getMinutes();
                        } else {
                            newDate += date.getMinutes();
                        }
                    } else if (field == 's') {
                        if (date.getSeconds() < 10) {
                            newDate += '0' + date.getSeconds();
                        } else {
                            newDate += date.getSeconds();
                        }
                    } else if (field == 'A' || field == 'a') {
                        if (date.getHours() > 11) {
                            newDate += field == 'A' ? 'PM' : 'pm';
                        } else {
                            newDate += field == 'A' ? 'AM' : 'am';
                        }
                    } else {
                        newDate += field;
                    }
                }
                return newDate;
            },
            jsonDecode(e) {
                if (e) {
                    const data = JSON.parse(e);
                    return data;
                } else {
                    return false;
                }
            },
            async presentAlert(message) {
                const alert = await alertController
                    .create({
                        header: 'Alert!',
                        message: message,
                        buttons: ['OK'],
                    });
                await alert.present();
            },
        },
    });
</script>

<style scoped>
/* App Badges styling */
.app-id-badge {
  background: var(--ion-color-light-shade, #e0e0e0);
  color: var(--ion-color-dark, #222);
  padding: 4px 8px;
  border-radius: 6px;
  font-family: monospace;
  font-weight: bold;
  font-size: 13px;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

.status-badge.pending {
  background-color: #fef3c7;
  color: #d97706;
  border: 1px solid #fde68a;
}

.status-badge.approved {
  background-color: #d1fae5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-badge.declined {
  background-color: #ffe4e6;
  color: #e11d48;
  border: 1px solid #fecdd3;
}

/* Table styling updates */
.custom-datatable {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.company-cell {
  display: flex;
  align-items: center;
}

.price-text {
  font-weight: 600;
  color: var(--ion-color-dark);
}

.discount-text {
  font-weight: 600;
  color: var(--ion-color-success-shade, #28ba62);
}

.due-text {
  font-weight: 700;
  color: var(--ion-color-danger-shade, #cf3c4f);
}

.details-button {
  --padding-start: 8px;
  --padding-end: 8px;
  font-weight: 600;
  font-size: 13px;
}

.action-buttons-container {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-btn {
  --border-radius: 8px;
  font-weight: 600;
  margin: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Skeleton Loading Pulse */
.skeleton-card {
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

/* Detail Card and Modal Details styles */
.detail-card {
  background: var(--ion-color-light, #f4f5f8);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.03);
}

.company-title {
  margin: 0 0 6px 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--ion-color-dark);
}

.app-desc {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--ion-color-step-600, #666);
  line-height: 1.4;
}

.badge-row {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
}

.grid-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  border-top: 1px solid var(--ion-color-light-shade, #e0e0e0);
  border-bottom: 1px solid var(--ion-color-light-shade, #e0e0e0);
  padding: 16px 0;
  margin-bottom: 16px;
}

.grid-item {
  display: flex;
  flex-direction: column;
}

.grid-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--ion-color-step-500, #888);
  font-weight: 600;
  margin-bottom: 4px;
}

.grid-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--ion-color-dark);
}

.grid-value.highlight {
  color: var(--ion-color-success-shade, #28ba62);
}

.grid-value.due {
  color: var(--ion-color-danger-shade, #cf3c4f);
}

.metadata-footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--ion-color-step-500, #888);
}

/* Timeline Stepper UI/UX */
.timeline-container {
  margin-top: 10px;
  padding: 10px 5px;
}

.timeline-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ion-color-dark);
  margin-bottom: 20px;
}

.timeline-stepper {
  display: flex;
  flex-direction: column;
  padding-left: 10px;
}

.timeline-step {
  position: relative;
  display: flex;
  gap: 20px;
  padding-bottom: 24px;
}

.timeline-step:last-child {
  padding-bottom: 10px;
}

.timeline-line {
  position: absolute;
  left: 17px;
  top: 36px;
  bottom: 0;
  width: 2px;
  background: var(--ion-color-light-shade, #e0e0e0);
  z-index: 1;
}

.timeline-node {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ion-color-light-shade, #e0e0e0);
  color: var(--ion-color-step-600, #666);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  z-index: 2;
  box-shadow: 0 3px 6px rgba(0,0,0,0.05);
}

.timeline-node.pending {
  background: #fef3c7;
  color: #d97706;
  border: 2px solid #fde68a;
}

.timeline-node.approved {
  background: #d1fae5;
  color: #059669;
  border: 2px solid #a7f3d0;
}

.timeline-node.declined {
  background: #ffe4e6;
  color: #e11d48;
  border: 2px solid #fecdd3;
}

.timeline-content {
  flex: 1;
  background: var(--ion-color-light, #f4f5f8);
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.approver-name {
  font-weight: 600;
  font-size: 15px;
  color: var(--ion-color-dark);
}

.step-status-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: capitalize;
}

.step-status-tag.pending {
  background: #fef3c7;
  color: #d97706;
}

.step-status-tag.approved {
  background: #d1fae5;
  color: #059669;
}

.step-status-tag.declined {
  background: #ffe4e6;
  color: #e11d48;
}

.timeline-message {
  font-style: italic;
  font-size: 13px;
  color: var(--ion-color-step-700, #444);
  margin-top: 4px;
  background: rgba(255, 255, 255, 0.6);
  padding: 6px 10px;
  border-radius: 6px;
}

.timeline-time {
  font-size: 11px;
  color: var(--ion-color-step-500, #888);
  margin-top: 8px;
}

/* Action panel inside modal sheet */
.action-panel {
  background: #f8fafc;
  border: 1px dashed var(--ion-color-primary-tint, #4c8dff);
  border-radius: 12px;
  padding: 16px;
  margin-top: 20px;
  text-align: center;
}

.action-panel h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ion-color-dark);
}

.action-panel p {
  margin: 0 0 16px 0;
  font-size: 13px;
  color: var(--ion-color-step-600, #666);
}

.action-buttons-row {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.action-buttons-row .action-btn {
  flex: 1;
}

/* Tabs customization */
#ionSegmentScroll {
  --background: transparent;
  padding: 8px;
  margin-bottom: 8px;
}

ion-segment-button {
  --indicator-color: var(--ion-color-primary);
  --color-checked: var(--ion-color-primary-contrast);
  --background-checked: var(--ion-color-primary);
  --border-radius: 10px;
  margin: 0 4px;
  font-weight: 600;
}
</style>
